/**
 * ManageSubscriptionPage Store
 * Zustand store for managing subscription state
 */

import { create } from 'zustand';
import type { SubscriptionInfo } from '@/application/queries/subscription';

/**
 * Initial state values
 */
const initialState = {
  data: null as SubscriptionInfo | null,
  loading: true,
  error: null as Error | null,
  upgrading: false,
  upgradeError: null as Error | null,
};

/**
 * Store state interface
 */
interface ManageSubscriptionState {
  // Data
  data: SubscriptionInfo | null;
  loading: boolean;
  error: Error | null;

  // Upgrade state
  upgrading: boolean;
  upgradeError: Error | null;

  // Actions
  setData: (data: SubscriptionInfo) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  setUpgrading: (upgrading: boolean) => void;
  setUpgradeError: (error: Error | null) => void;
  reset: () => void;

  // For testing
  _reset: () => void;
}

/**
 * Zustand store for ManageSubscriptionPage
 */
export const useManageSubscriptionStore = create<ManageSubscriptionState>((set) => ({
  // Initial state
  ...initialState,

  // Actions
  setData: (data) =>
    set({
      data,
      loading: false,
      error: null,
    }),

  setLoading: (loading) => set({ loading }),

  setError: (error) =>
    set({
      error,
      loading: false,
    }),

  setUpgrading: (upgrading) => set({ upgrading }),

  setUpgradeError: (upgradeError) =>
    set({
      upgradeError,
      upgrading: false,
    }),

  reset: () =>
    set({
      ...initialState,
      loading: true,
    }),

  // Reset to initial state (for testing)
  _reset: () => set(initialState),
}));
